import { createSlice } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";

const TOKEN_KEY    = "emp_web_token";
const USER_KEY     = "emp_web_user_id";
const IS_ADMIN_KEY = "emp_web_is_admin";

// Re-hydrate from localStorage on each page load. Prod-grade should also check
// exp, but emp_api short-circuits stale tokens via 401, so the axios
// interceptor (lib/api.ts) handles cleanup if the token is dead.
const loadInitial = (): AuthState => ({
  token:    typeof window !== "undefined" ? localStorage.getItem(TOKEN_KEY)  : null,
  userId:   typeof window !== "undefined" ? localStorage.getItem(USER_KEY)   : null,
  isAdmin:  typeof window !== "undefined" ? localStorage.getItem(IS_ADMIN_KEY) === "1" : false,
});

export interface AuthState {
  token:   string | null;
  userId:  string | null;
  isAdmin: boolean;
}

const slice = createSlice({
  name: "auth",
  initialState: loadInitial(),
  reducers: {
    loggedIn(state, action: PayloadAction<{ token: string; userId: string; isAdmin: boolean }>) {
      state.token   = action.payload.token;
      state.userId  = action.payload.userId;
      state.isAdmin = action.payload.isAdmin;
      localStorage.setItem(TOKEN_KEY,    action.payload.token);
      localStorage.setItem(USER_KEY,     action.payload.userId);
      localStorage.setItem(IS_ADMIN_KEY, action.payload.isAdmin ? "1" : "0");
    },
    loggedOut(state) {
      state.token   = null;
      state.userId  = null;
      state.isAdmin = false;
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(USER_KEY);
      localStorage.removeItem(IS_ADMIN_KEY);
    },
  },
});

export const { loggedIn, loggedOut } = slice.actions;
export const authReducer = slice.reducer;

// Selector helpers — keep components ignorant of slice shape.
export const selectIsAuthenticated = (s: { auth: AuthState }): boolean => !!s.auth.token;
export const selectUserId          = (s: { auth: AuthState }) => s.auth.userId;
export const selectToken           = (s: { auth: AuthState }) => s.auth.token;
export const selectIsAdmin         = (s: { auth: AuthState }) => s.auth.isAdmin;
